import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import { Form, Row, Col } from 'antd';
import BasePage from 'components/BasePage';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import FormDatePicker from 'components-desktop/FormDatePicker';
import FormInput from 'components-desktop/FormInput';
import FormInputNumber from 'components-desktop/FormInputNumber';
import FormSelect from 'components-desktop/FormSelect';
import FormTextArea from 'components-desktop/FormTextArea';
import Panel from 'components-desktop/Panel';
import helpers from 'misc/helpers';

const propTypes = {
  form: PropTypes.object.isRequired,
};

@autobind
class AddPaymentPage extends BasePage {
  constructor(props) {
    super(props);

    this.state = {
      companies: [],
      isAddButtonEnable: true,
    };
  }

  componentDidMount() {
    this.getAddCriteria();
  }

  getTitle() {
    return 'Add a New Payment';
  }

  async getAddCriteria() {
    const promise1 = this.callServiceForObjects('/company/get_id_names');

    const results = await Promise.all([promise1]);

    this.setState({
      companies: results[0],
    });
  }

  async addPayment(values) {
    try {
      this.setState({
        isAddButtonEnable: false,
      });

      const params = {
        companyId: helpers.extraFormSelectValue(values, 'companyId'),
        checkNumber: helpers.extraFormInputValue(values, 'checkNumber'),
        paymentMethod: helpers.extraFormSelectValue(values, 'paymentMethod'),
        checkDate: helpers.extraFormDateValue(values, 'checkDate'),
        receivedDate: helpers.extraFormDateValue(values, 'receivedDate'),
        amount: helpers.extraFormInputNumberValue(values, 'amount'),
        remark: helpers.extraFormTextAreaValue(values, 'remark'),
      };

      const paymentId = await this.callServiceForLong('/payment/add', params);

      this.notifySuccess('Success',
        'The payment has been added, you will now be re-directed to its details page...');

      // Go to the new payment's details page.
      setTimeout(() => {
        window.location = `/payments/payment_details?id=${paymentId}`;
      }, 2000);
    } finally {
      this.setState({
        isAddButtonEnable: true,
      });
    }
  }

  handleSubmit(e) {
    e.preventDefault();

    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        this.addPayment(values);
      }
    });
  }

  onResetClick() {
    window.location.reload();
  }

  renderContent() {
    const { form } = this.props;
    const { companies, isAddButtonEnable } = this.state;

    return (
      <FlexColContainer
        width="match_parent"
        height="wrap_content"
        alignItems="center"
      >
        <Panel
          width="match_parent"
          height="wrap_content"
          margin="0 0 24px 0"
          color="yellow_gold"
          title="Add a New Payment"
          icon="money-collect"
        >
          <styledComp.AntdForm
            width="match_parent"
            height="wrap_content"
            onSubmit={this.handleSubmit}
          >
            {/* Content */}
            <FlexColContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
            >
              <Row type="flex" gutter={24}>
                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="companyId"
                    label="Company"
                    placeholder="Select..."
                    options={companies}
                    isRequired
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="checkNumber"
                    label="Check Number"
                    isRequired
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormSelect
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="paymentMethod"
                    label="Payment Method"
                    placeholder="Select..."
                    allowClear={false}
                    initialValue="Check"
                    options={['Check', 'Other']}
                    isRequired
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormDatePicker
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="checkDate"
                    label="Check Date"
                    placeholder="mm/dd/yyyy"
                    isRequired
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormDatePicker
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="receivedDate"
                    label="Received Date"
                    placeholder="mm/dd/yyyy"
                    isRequired
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInputNumber
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="amount"
                    label="Total Amount"
                    placeholder="12.34"
                    min={-999999999} // Sunshine requested allowing negative payment amount.
                    step={0.01}
                    precision={2}
                    // formatter={value => `$${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    // parser={value => value.replace(/\$\s?|(,*)/g, '')}
                    isRequired
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                  <FormTextArea
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="remark"
                    label="Remark"
                    rows={3}
                    max={2000}
                  />
                </Col>
              </Row>
            </FlexColContainer>

            {/* Footer */}
            <FlexRowContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
              justifyContent="center"
              backgroundColor="containerGrayBackground"
            >
              <CustomButton
                width="wrap_content"
                height="32px"
                margin="0 4px 0 0"
                backgroundColor="yellow_gold"
                fontSize="14px"
                type="primary"
                loading={!isAddButtonEnable}
                htmlType="submit"
              >
                ADD PAYMENT
              </CustomButton>

              <styledComp.AntdButton
                width="wrap_content"
                height="32px"
                fontSize="14px"
                type="default"
                onClick={this.onResetClick}
              >
                RESET
              </styledComp.AntdButton>
            </FlexRowContainer>
          </styledComp.AntdForm>
        </Panel>
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
AddPaymentPage.propTypes = propTypes;

export default Form.create()(AddPaymentPage);
